import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField
} from "@mui/material";
import { styled } from "@mui/system";
import { useEffect } from "react";
import { NumericFormat } from "react-number-format";

const StyledTextField = styled(TextField)({
  minWidth: "80px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E9EAEC",
    borderRadius: "10px"
  },
  "& .MuiInputBase-root.Mui-disabled": {
    backgroundColor: "rgb(255, 255, 255)",
    borderColor: "rgb(179, 179, 179)",
    outline: "none",
    "& fieldset": {
      borderColor: "rgb(179, 179, 179)",
      borderWidth: "0.5px"
    }
  }
});

export const Section2 = ({
  formState,
  handleChange,
  handleSectionComplete,
  reportId,
  isEditingEnabled
}) => {
  useEffect(() => {
    // Calculate totals for both gospelSpread and newMembers sections
    const gospelCategories = ["heard_gospel"];
    const newMemberCategories = ["accepted", "baptized", "in_salvation_class"];

    // Calculate updates for gospelSpread
    const gospelUpdates = gospelCategories.reduce((acc, category) => {
      const male = Number(formState.gospelSpread[category].male) || 0;
      const female = Number(formState.gospelSpread[category].female) || 0;
      const total = male + female;

      if (total !== formState.gospelSpread[category].total) {
        acc[category] = {
          ...formState.gospelSpread[category],
          total
        };
      }
      return acc;
    }, {});

    // Calculate updates for newMembers
    const newMemberUpdates = newMemberCategories.reduce((acc, category) => {
      const male = Number(formState.newMembers[category].male) || 0;
      const female = Number(formState.newMembers[category].female) || 0;
      const total = male + female;

      if (total !== formState.newMembers[category].total) {
        acc[category] = {
          ...formState.newMembers[category],
          total
        };
      }
      return acc;
    }, {});

    // Update gospelSpread if there are changes
    if (Object.keys(gospelUpdates).length > 0) {
      handleChange("gospelSpread", "all", gospelUpdates);
    }

    // Update newMembers if there are changes
    if (Object.keys(newMemberUpdates).length > 0) {
      handleChange("newMembers", "all", {
        ...newMemberUpdates
      });
    }
  }, [
    formState.gospelSpread.heard_gospel.male,
    formState.gospelSpread.heard_gospel.female,
    formState.newMembers.accepted.male,
    formState.newMembers.accepted.female,
    formState.newMembers.baptized.male,
    formState.newMembers.baptized.female,
    formState.newMembers.in_salvation_class.male,
    formState.newMembers.in_salvation_class.female,
    formState.memberCounts.total
  ]);

  return (
    <Stack spacing={2}>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell></TableCell>
              <TableCell align="center">ወንድ</TableCell>
              <TableCell align="center">ሴት</TableCell>
              <TableCell align="center">ድምር</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>ወንጌል የሰሙ ብዛት</TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.gospelSpread.heard_gospel.male}
                  onValueChange={values => {
                    handleChange("gospelSpread", "heard_gospel", {
                      ...formState.gospelSpread.heard_gospel,
                      male: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.gospelSpread.heard_gospel.female}
                  onValueChange={values => {
                    handleChange("gospelSpread", "heard_gospel", {
                      ...formState.gospelSpread.heard_gospel,
                      female: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.gospelSpread.heard_gospel.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>ጌታን የተቀበሉ ምዕመናን ብዛት</TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.newMembers.accepted.male}
                  onValueChange={values => {
                    handleChange("newMembers", "accepted", {
                      ...formState.newMembers.accepted,
                      male: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.newMembers.accepted.female}
                  onValueChange={values => {
                    handleChange("newMembers", "accepted", {
                      ...formState.newMembers.accepted,
                      female: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.newMembers.accepted.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>የውሃ ጥምቀት የወሰዱ ምዕመናን ብዛት</TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.newMembers.baptized.male}
                  onValueChange={values => {
                    handleChange("newMembers", "baptized", {
                      ...formState.newMembers.baptized,
                      male: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.newMembers.baptized.female}
                  onValueChange={values => {
                    handleChange("newMembers", "baptized", {
                      ...formState.newMembers.baptized,
                      female: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.newMembers.baptized.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>የድነት ት/ት እየተማሩ ያሉ ምዕመናን ብዛት</TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.newMembers.in_salvation_class.male}
                  onValueChange={values => {
                    handleChange("newMembers", "in_salvation_class", {
                      ...formState.newMembers.in_salvation_class,
                      male: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.newMembers.in_salvation_class.female}
                  onValueChange={values => {
                    handleChange("newMembers", "in_salvation_class", {
                      ...formState.newMembers.in_salvation_class,
                      female: values.floatValue
                    });
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
              <TableCell align="center">
                <NumericFormat
                  customInput={StyledTextField}
                  thousandSeparator
                  allowNegative={false}
                  value={formState.newMembers.in_salvation_class.total}
                  inputProps={{
                    readOnly: true
                  }}
                  fullWidth
                  disabled={!isEditingEnabled}
                />
              </TableCell>
            </TableRow>
            {/* Hide this percentage field */}
            {/* <TableRow>
            <TableCell>
              <Typography>የምዕመናን ዕድገት በፐርሰንት ሲሰላ</Typography>
            </TableCell>
            <TableCell colSpan={3}>
              <StyledTextField
                fullWidth
                type="number"
                defaultValue={formState.newMembers.growth_percentage}
                onBlur={e =>
                  handleChange("newMembers", "growth_percentage", {
                    ...formState.newMembers.growth_percentage,
                    growth_percentage: Number(e.target.value)
                  })
                }
              />
            </TableCell>
          </TableRow> */}
          </TableBody>
        </Table>
      </TableContainer>
      {!reportId && (
        <Stack alignItems="flex-start">
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSectionComplete(3)}
          >
            ቀጥል
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
